/**
 * Super-admin Plan Tiers — plans-as-data CRUD.
 *
 * Manage the SaaS plan catalog (free / lite / pro / enterprise + any custom
 * tiers the founder defines). Each tier sets the default price + seat-cap
 * that the per-tenant ChangePlanDialog reads on plan selection. Add a brand-
 * new tier with `code` = lowercase slug (e.g. "premium", "enterprise_plus")
 * — Society.plan stores the code as VARCHAR, no schema migration needed.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';

interface PlanTierRow {
  code: string;
  label: string;
  description: string | null;
  price_inr_per_month: number;
  seats_cap_units: number | null;
  sort_order: number;
  is_active: boolean;
  tenants_count?: number;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  items: PlanTierRow[];
}

const CreateSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits, _ only (e.g. "premium")'),
  label: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).optional(),
  price_inr_per_month: z.coerce.number().int().min(0).max(10_000_000),
  seats_cap_units: z
    .union([z.coerce.number().int().min(1).max(1_000_000), z.literal('').transform(() => null)])
    .optional()
    .nullable(),
  sort_order: z.coerce.number().int().min(0).max(10_000).default(100)
});

export function SuperAdminPlansPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PlanTierRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlanTierRow | null>(null);

  const list = useQuery({
    queryKey: ['superadmin', 'plan-tiers'],
    queryFn: () => get<ListResponse>('/superadmin/plan-tiers')
  });

  const items = list.data?.items ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['superadmin', 'plan-tiers'] });
    void queryClient.invalidateQueries({ queryKey: ['superadmin', 'societies'] });
  };

  const createMutation = useMutation({
    mutationFn: (body: z.infer<typeof CreateSchema>) => post<PlanTierRow>('/superadmin/plan-tiers', body),
    onSuccess: () => {
      toast.success('Plan tier created');
      setCreateOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const updateMutation = useMutation({
    mutationFn: ({ code, body }: { code: string; body: Partial<PlanTierRow> }) =>
      patch<PlanTierRow>(`/superadmin/plan-tiers/${code}`, body),
    onSuccess: () => {
      toast.success('Plan tier updated');
      setEditing(null);
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => del<void>(`/superadmin/plan-tiers/${code}`),
    onSuccess: () => {
      toast.success('Plan tier deleted');
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <>
      <PageHeader
        title="Plan tiers"
        description="Manage the subscription catalog. Each tier sets the default price + unit cap that show up in the per-tenant Change-plan dialog. Add a new tier with a unique lowercase code."
        actions={
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4" /> Add tier
              </Button>
            </DialogTrigger>
            <CreateDialog
              onCancel={() => setCreateOpen(false)}
              onSubmit={(body) => createMutation.mutate(body)}
              pending={createMutation.isPending}
            />
          </Dialog>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            {list.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : items.length === 0 ? (
              <EmptyState title="No plan tiers" description="Create your first tier to start charging tenants." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">Price / mo</TableHead>
                    <TableHead className="text-right">Seats cap</TableHead>
                    <TableHead className="text-right">Order</TableHead>
                    <TableHead className="text-right">Tenants</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((t) => (
                    <TableRow key={t.code}>
                      <TableCell>
                        <div className="font-medium text-vt-text">{t.label}</div>
                        {t.description ? (
                          <div className="max-w-[300px] truncate text-vt-12 text-vt-text-soft" title={t.description}>
                            {t.description}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <code className="rounded-vt-sm bg-vt-surface-2 px-vt-1 py-[1px] font-mono text-vt-12">
                          {t.code}
                        </code>
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13">
                        {t.price_inr_per_month === 0
                          ? 'Free'
                          : `₹${t.price_inr_per_month.toLocaleString('en-IN')}`}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13">
                        {t.seats_cap_units === null ? '∞' : t.seats_cap_units}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text-soft">
                        {t.sort_order}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13">
                        {t.tenants_count ?? 0}
                      </TableCell>
                      <TableCell>
                        <Badge variant={t.is_active ? 'ok' : 'muted'}>{t.is_active ? 'Active' : 'Inactive'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-vt-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing(t)}
                            title="Edit tier"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              updateMutation.mutate({
                                code: t.code,
                                body: { is_active: !t.is_active }
                              })
                            }
                            title={t.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {t.is_active ? (
                              <PowerOff className="h-3.5 w-3.5" />
                            ) : (
                              <Power className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if ((t.tenants_count ?? 0) > 0) {
                                toast.error(
                                  `${t.tenants_count} tenant(s) on this tier — deactivate instead.`
                                );
                                return;
                              }
                              setDeleteTarget(t);
                            }}
                            title="Delete tier (only if 0 tenants)"
                            disabled={(t.tenants_count ?? 0) > 0}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {editing ? (
        <Dialog open onOpenChange={(open) => !open && setEditing(null)}>
          <EditDialog
            tier={editing}
            onCancel={() => setEditing(null)}
            onSubmit={(body) =>
              updateMutation.mutate({ code: editing.code, body })
            }
            pending={updateMutation.isPending}
          />
        </Dialog>
      ) : null}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => (v ? null : setDeleteTarget(null))}
        title="Delete plan tier"
        description={
          deleteTarget ? (
            <>
              Delete tier <strong>{deleteTarget.code}</strong>? This action cannot be undone.
              Only allowed when no tenants are on this tier.
            </>
          ) : null
        }
        confirmLabel="Delete tier"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget.code, {
            onSuccess: () => setDeleteTarget(null)
          });
        }}
      />
    </>
  );
}

function CreateDialog({
  onCancel,
  onSubmit,
  pending
}: {
  onCancel: () => void;
  onSubmit: (body: z.infer<typeof CreateSchema>) => void;
  pending: boolean;
}): JSX.Element {
  const [form, setForm] = useState({
    code: '',
    label: '',
    description: '',
    price_inr_per_month: '',
    seats_cap_units: '',
    sort_order: '100'
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});

  const submit = (): void => {
    const parsed = CreateSchema.safeParse(form);
    if (!parsed.success) {
      const next: Partial<Record<keyof typeof form, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof typeof form;
        next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit(parsed.data);
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Add plan tier</DialogTitle>
        <DialogDescription>
          Define a new subscription tier. The code is the slug stored on each tenant — keep it short and
          lowercase (e.g. <code>premium</code>, <code>society_plus</code>).
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-vt-3">
        <div>
          <Label htmlFor="code">Code (slug)</Label>
          <Input
            id="code"
            placeholder="premium"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
          />
          {errors.code ? <p className="mt-vt-1 text-vt-12 text-vt-alert">{errors.code}</p> : null}
        </div>
        <div>
          <Label htmlFor="label">Label</Label>
          <Input
            id="label"
            placeholder="Premium"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          {errors.label ? <p className="mt-vt-1 text-vt-12 text-vt-alert">{errors.label}</p> : null}
        </div>
        <div>
          <Label htmlFor="description">Description (optional)</Label>
          <Input
            id="description"
            placeholder="Mid-market — for societies up to 250 units"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-vt-3">
          <div>
            <Label htmlFor="price">Price (₹/month)</Label>
            <Input
              id="price"
              type="number"
              min="0"
              placeholder="999"
              value={form.price_inr_per_month}
              onChange={(e) => setForm({ ...form, price_inr_per_month: e.target.value })}
            />
            {errors.price_inr_per_month ? (
              <p className="mt-vt-1 text-vt-12 text-vt-alert">{errors.price_inr_per_month}</p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="seats">Seats cap (units)</Label>
            <Input
              id="seats"
              type="number"
              min="1"
              placeholder="Blank for unlimited"
              value={form.seats_cap_units}
              onChange={(e) => setForm({ ...form, seats_cap_units: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="sort_order">Sort order</Label>
          <Input
            id="sort_order"
            type="number"
            min="0"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
          />
          <p className="mt-vt-1 text-vt-12 text-vt-text-soft">
            Lower = earlier in the picker. Free=10, Lite=20, Pro=30, Enterprise=40.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={submit} loading={pending} loadingText="Creating…">
          Create tier
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditDialog({
  tier,
  onCancel,
  onSubmit,
  pending
}: {
  tier: PlanTierRow;
  onCancel: () => void;
  onSubmit: (body: Partial<PlanTierRow>) => void;
  pending: boolean;
}): JSX.Element {
  const [form, setForm] = useState({
    label: tier.label,
    description: tier.description ?? '',
    price_inr_per_month: String(tier.price_inr_per_month),
    seats_cap_units: tier.seats_cap_units === null ? '' : String(tier.seats_cap_units),
    sort_order: String(tier.sort_order)
  });

  const submit = (): void => {
    onSubmit({
      label: form.label,
      description: form.description.trim() ? form.description : null,
      price_inr_per_month: Number.parseInt(form.price_inr_per_month, 10) || 0,
      seats_cap_units: form.seats_cap_units.trim() ? Number.parseInt(form.seats_cap_units, 10) : null,
      sort_order: Number.parseInt(form.sort_order, 10) || 100
    });
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Edit "{tier.label}"</DialogTitle>
        <DialogDescription>
          Code <code className="rounded-vt-sm bg-vt-surface-2 px-vt-1 py-[1px] font-mono text-vt-12">{tier.code}</code> is immutable
          (tenants reference it). {tier.tenants_count} tenant{tier.tenants_count === 1 ? '' : 's'} currently on
          this tier.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-vt-3">
        <div>
          <Label htmlFor="e-label">Label</Label>
          <Input id="e-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="e-desc">Description</Label>
          <Input
            id="e-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-vt-3">
          <div>
            <Label htmlFor="e-price">Price (₹/month)</Label>
            <Input
              id="e-price"
              type="number"
              min="0"
              value={form.price_inr_per_month}
              onChange={(e) => setForm({ ...form, price_inr_per_month: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="e-seats">Seats cap</Label>
            <Input
              id="e-seats"
              type="number"
              min="1"
              placeholder="Blank for unlimited"
              value={form.seats_cap_units}
              onChange={(e) => setForm({ ...form, seats_cap_units: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="e-order">Sort order</Label>
          <Input
            id="e-order"
            type="number"
            min="0"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={submit} loading={pending} loadingText="Saving…">
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
